import React, { useState } from 'react';
import { Mail, MapPin, Phone, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    inquiryType: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate that inquiry type is selected
      if (!formData.inquiryType) {
        toast({
          title: 'Error',
          description: 'Please select an inquiry type.',
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }

      // Send form data to backend
      const response = await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        inquiry_type: formData.inquiryType,
        message: formData.message,
      });

      if (response.status === 200) {
        toast({
          title: 'Message Sent!',
          description: 'Thank you for reaching out. Our team at sales@harpalyk.com will get back to you soon.',
        });
        
        // Clear form
        setFormData({
          name: '',
          email: '',
          phone: '',
          inquiryType: '',
          message: '',
        });
      }
    } catch (error) {
      console.error('Contact form submission error:', error);
      toast({
        title: 'Error',
        description: 'Failed to send message. Please try again or contact us directly at sales@harpalyk.com',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-32 bg-gray-50 px-6 lg:px-12">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12 animate-in fade-in slide-in-from-bottom duration-1000">
          <h2 className="text-5xl lg:text-6xl font-light tracking-tight text-gray-900 mb-4">
            Schedule a Product Demo
          </h2>
          <p className="text-xl text-gray-600 font-light leading-relaxed">
            Get in touch with our team to experience our intelligent robotics solutions firsthand
          </p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Info */}
          <div className="space-y-8 animate-in fade-in slide-in-from-left duration-1000">
            <h3 className="text-3xl font-light tracking-tight text-gray-900 mb-6">
              Get in Touch
            </h3>
            <p className="text-lg text-gray-600 font-light leading-relaxed">
              Ready to transform your home with intelligent robotics? We'd love to hear from you.
            </p>

            <div className="space-y-6 pt-8">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-blue-100 to-emerald-100 flex items-center justify-center">
                  <Mail className="text-gray-900" size={20} />
                </div>
                <div>
                  <h4 className="text-lg font-light text-gray-900 mb-1">Email</h4>
                  <p className="text-gray-600">sales@harpalyk.com</p>
                  <p className="text-gray-600">support@harpalyk.com</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-blue-100 to-emerald-100 flex items-center justify-center">
                  <Phone className="text-gray-900" size={20} />
                </div>
                <div>
                  <h4 className="text-lg font-light text-gray-900 mb-1">Phone</h4>
                  <p className="text-gray-600"><a href="tel:+917011370322" data-testid="contact-phone-1" className="hover:text-gray-900">+91-7011370322</a></p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-blue-100 to-emerald-100 flex items-center justify-center">
                  <MapPin className="text-gray-900" size={20} />
                </div>
                <div>
                  <h4 className="text-lg font-light text-gray-900 mb-1">Headquarters</h4>
                  <p className="text-gray-600">Gurugram, India</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white p-10 rounded-2xl shadow-lg animate-in fade-in slide-in-from-right duration-1000 delay-300">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="h-12"
                />
              </div>

              <div>
                <Input
                  type="email"
                  name="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="h-12"
                />
              </div>

              <div>
                <Input
                  type="tel"
                  name="phone"
                  placeholder="Phone Number (Optional)"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="h-12"
                />
              </div>

              <div>
                <Select onValueChange={(value) => setFormData({ ...formData, inquiryType: value })}>
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Select Inquiry Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="product">Product Inquiry</SelectItem>
                    <SelectItem value="support">Technical Support</SelectItem>
                    <SelectItem value="partnership">Partnership</SelectItem>
                    <SelectItem value="general">General Question</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Textarea
                  name="message"
                  placeholder="Your Message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={5}
                  className="resize-none"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gray-900 text-white hover:bg-gray-800 h-12 text-lg group transition-all duration-300 hover:scale-105"
              >
                {loading ? 'Sending...' : 'Send Message'}
                <Send className="ml-2 group-hover:translate-x-1 transition-transform duration-300" size={18} />
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;